import { useEffect, useRef, useState } from 'react';
import {
  CONSIGNMENT_PAYMENT_METHODS,
  consignmentPaymentStatus,
  consignmentBankReceiptReference,
  type ConsignmentFinanceDetail,
  type ConsignmentFinanceMutation,
  type ConsignmentFinanceSummary,
  type ConsignmentPaymentMethod,
  type ConsignmentStatement,
} from '@tcvs/shared';
import { useConsignmentApi } from './consignment-api';
import { consignmentErrorMessage, formatMoneyMinor as money } from './consignment-ui';
import { ConsignmentBankReceiptReview } from './ConsignmentBankReceiptReview';

export function paymentMinor(text: string): number | undefined {
  if (!/^\d{1,10}(\.\d{1,2})?$/.test(text)) return undefined;
  const [whole, fraction = ''] = text.split('.');
  const n = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  return Number.isSafeInteger(n) && n > 0 ? n : undefined;
}
export function ConsignmentPayments({
  statement,
  onSummary,
}: {
  statement: ConsignmentStatement;
  onSummary: (s: ConsignmentFinanceSummary) => void;
}): JSX.Element {
  const api = useConsignmentApi(),
    [data, setData] = useState<ConsignmentFinanceDetail>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<string>(),
    [notice, setNotice] = useState<string>();
  const [amount, setAmount] = useState(''),
    [date, setDate] = useState(new Date().toISOString().slice(0, 10)),
    [method, setMethod] = useState<ConsignmentPaymentMethod>('BANK_TRANSFER'),
    [reference, setReference] = useState(''),
    [transferred, setTransferred] = useState(false);
  const [bankReviewed, setBankReviewed] = useState(false),
    [bankReference, setBankReference] = useState('');
  const [reason, setReason] = useState(''),
    [reviewed, setReviewed] = useState(false),
    [voidId, setVoidId] = useState<string>(),
    [voidReason, setVoidReason] = useState('');
  const alive = useRef(true),
    pending = useRef(false),
    request = useRef<{ signature: string; id: string }>();
  const callback = useRef(onSummary);
  callback.current = onSummary;
  async function load(cursor?: string) {
    const r = await api.getStatementFinance(statement.consignorId, statement.statementId, cursor);
    if (!r.ok) throw Error(consignmentErrorMessage(r));
    if (!alive.current) return;
    setData((old) =>
      cursor && old
        ? {
            ...r.data,
            events: [
              ...old.events,
              ...r.data.events.filter((e) => !old.events.some((o) => o.requestId === e.requestId)),
            ],
          }
        : r.data,
    );
    callback.current(r.data.summary);
  }
  async function run(action: () => Promise<void>) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    setError(undefined);
    setNotice(undefined);
    try {
      await action();
    } catch (e) {
      if (alive.current)
        setError(e instanceof Error ? e.message : 'Request failed. Refresh and retry.');
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  useEffect(() => {
    alive.current = true;
    void run(() => load());
    return () => {
      alive.current = false;
    };
  }, [api, statement.statementId, statement.consignorId]);
  async function mutate(
    body:
      | Omit<
          Extract<ConsignmentFinanceMutation, { operation: 'RECORD_STATEMENT_PAYMENT' }>,
          'requestId'
        >
      | Omit<
          Extract<ConsignmentFinanceMutation, { operation: 'APPROVE_STATEMENT_ADJUSTMENT' }>,
          'requestId'
        >
      | Omit<
          Extract<ConsignmentFinanceMutation, { operation: 'VOID_STATEMENT_PAYMENT' }>,
          'requestId'
        >,
  ) {
    const signature = JSON.stringify(body);
    if (request.current?.signature !== signature)
      request.current = { signature, id: crypto.randomUUID() };
    const r = await api.mutate<ConsignmentFinanceSummary>({
      ...body,
      requestId: request.current.id,
    });
    if (!r.ok) throw Error(consignmentErrorMessage(r));
    if (!alive.current) return;
    request.current = undefined;
    callback.current(r.data);
    setAmount('');
    setReference('');
    setTransferred(false);
    setBankReviewed(false);
    setBankReference('');
    setReviewed(false);
    setReason('');
    setVoidId(undefined);
    setVoidReason('');
    setNotice(
      body.operation === 'RECORD_STATEMENT_PAYMENT'
        ? 'External payment recorded. No money was sent by TCVS.'
        : body.operation === 'VOID_STATEMENT_PAYMENT'
          ? 'Payment record voided. No money was returned.'
          : 'Adjustment approved. Original statement amounts remain unchanged.',
    );
    await load();
  }
  const minor = paymentMinor(amount),
    blocked = !data || data.preview.needsReview || data.preview.blockers.length > 0;
  const paymentBlocked = blocked || !data || data.summary.balanceAmountMinor <= 0;
  const common = {
    consignorId: statement.consignorId,
    statementId: statement.statementId,
    version: data?.summary.version ?? 0,
  };
  return (
    <section className="tcvs-statement-finance" aria-label="Payments and adjustments">
      <div className="tcvs-statement-toolbar">
        <h4>Payments and adjustments</h4>
        <button disabled={busy} onClick={() => void run(() => load())}>
          Refresh payments
        </button>
      </div>
      {busy && <p role="status">Loading financial records...</p>}
      {error && (
        <p role="alert" className="tcvs-error">
          {error}
        </p>
      )}
      {notice && <p role="status">{notice}</p>}
      {data && (
        <>
          <p>
            <strong>{consignmentPaymentStatus(data.summary)}</strong>
          </p>
          <dl className="tcvs-detail-list">
            {Object.entries({
              'Approved proceeds': statement.totals.estimatedProceedsMinor,
              'Approved adjustments': data.summary.adjustmentAmountMinor,
              'Recorded payments': data.summary.paidAmountMinor,
              [data.summary.balanceAmountMinor < 0 ? 'Recovery due' : 'Outstanding']: Math.abs(
                data.summary.balanceAmountMinor,
              ),
            }).map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{money(value)}</dd>
              </div>
            ))}
          </dl>
          {data.preview.blockers.length > 0 && (
            <div role="alert">
              <h4>Reconciliation required</h4>
              <ul>
                {data.preview.blockers.map((b) => (
                  <li key={b}>{b}</li>
                ))}
              </ul>
            </div>
          )}
          {data.preview.needsReview && (
            <div className="tcvs-statement-actions">
              <h4>Adjustment preview: {money(data.preview.adjustmentAmountMinor)}</h4>
              <div className="tcvs-table-wrap">
                <table className="tcvs-table">
                  <thead>
                    <tr>
                      <th>Sale</th>
                      <th>Originally approved</th>
                      <th>Latest proceeds</th>
                      <th>Evidence (UTC)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.preview.lines.map((l) => (
                      <tr key={l.allocationId}>
                        <td>{l.title ?? l.cardId}</td>
                        <td>
                          {money(
                            statement.lines.find((o) => o.allocationId === l.allocationId)
                              ?.lifecycle.estimatedProceedsMinor ?? 0,
                          )}
                        </td>
                        <td>{money(l.lifecycle.estimatedProceedsMinor)}</td>
                        <td>{l.lifecycle.sourceModifiedAt.replace('T', ' ').slice(0, 19)}</td>
                      </tr>
                    ))}
                    {(data.preview.feeCharges ?? []).map((c) => (
                      <tr key={c.feeChargeId}>
                        <td>Unsold charge: {c.cardId}</td>
                        <td>
                          {money(
                            -(
                              statement.feeCharges?.find((o) => o.feeChargeId === c.feeChargeId)
                                ?.amountMinor ?? 0
                            ),
                          )}
                        </td>
                        <td>
                          {money(c.status === 'REVERSED' ? 0 : -c.amountMinor)} · {c.status}
                        </td>
                        <td>
                          {c.reversedAt ?? c.createdAt} · {c.reversalReason ?? c.evidenceReference}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <label>
                Adjustment reason
                <textarea
                  maxLength={500}
                  value={reason}
                  disabled={busy}
                  onChange={(e) => setReason(e.target.value)}
                />
              </label>
              <label>
                <input
                  type="checkbox"
                  checked={reviewed}
                  disabled={busy}
                  onChange={(e) => setReviewed(e.target.checked)}
                />{' '}
                I reviewed the latest order and charge evidence and this separate adjustment.
              </label>
              <button
                disabled={
                  busy || data.preview.blockers.length > 0 || !reviewed || reason.trim().length < 3
                }
                onClick={() =>
                  void run(() =>
                    mutate({
                      ...common,
                      operation: 'APPROVE_STATEMENT_ADJUSTMENT',
                      fingerprint: data.preview.fingerprint,
                      reason,
                      reviewed,
                    }),
                  )
                }
              >
                Approve adjustment
              </button>
            </div>
          )}
          <form
            className="tcvs-payment-form"
            onSubmit={(e) => {
              e.preventDefault();
              if (
                minor &&
                !busy &&
                !paymentBlocked &&
                minor <= data.summary.balanceAmountMinor &&
                transferred &&
                bankReviewed &&
                consignmentBankReceiptReference(bankReference) &&
                reference.trim().length >= 2 &&
                date
              )
                void run(() =>
                  mutate({
                    ...common,
                    operation: 'RECORD_STATEMENT_PAYMENT',
                    amountMinor: minor,
                    paymentDate: date,
                    bankReceiptReviewed: bankReviewed,
                    bankReceiptReference: bankReference.trim(),
                    method,
                    reference,
                    transferred,
                  }),
                );
            }}
          >
            <h4>Record external payment</h4>
            {data.summary.balanceAmountMinor <= 0 && (
              <p>
                No positive payment is due. Recovery balances require separate review; TCVS does not
                collect them automatically.
              </p>
            )}
            <ConsignmentBankReceiptReview
              checked={bankReviewed}
              reference={bankReference}
              disabled={busy || paymentBlocked}
              onChecked={setBankReviewed}
              onReference={setBankReference}
            />
            <div className="tcvs-payment-fields">
              <label>
                Amount (USD)
                <input
                  inputMode="decimal"
                  value={amount}
                  disabled={busy || paymentBlocked}
                  onChange={(e) => setAmount(e.target.value)}
                  required
                />
              </label>
              <label>
                Payment date
                <input
                  type="date"
                  value={date}
                  min={statement.approvedAt?.slice(0, 10)}
                  max={new Date().toISOString().slice(0, 10)}
                  disabled={busy || paymentBlocked}
                  onChange={(e) => setDate(e.target.value)}
                  required
                />
              </label>
              <label>
                Payment method
                <select
                  value={method}
                  disabled={busy || paymentBlocked}
                  onChange={(e) => setMethod(e.target.value as ConsignmentPaymentMethod)}
                >
                  {CONSIGNMENT_PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m.replaceAll('_', ' ')}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Transfer reference
                <input
                  value={reference}
                  maxLength={120}
                  disabled={busy || paymentBlocked}
                  onChange={(e) => setReference(e.target.value)}
                  required
                />
              </label>
            </div>
            <label>
              <input
                type="checkbox"
                checked={transferred}
                disabled={busy || paymentBlocked}
                onChange={(e) => setTransferred(e.target.checked)}
              />{' '}
              I confirm this money was already transferred outside TCVS.
            </label>
            <button
              disabled={
                busy ||
                paymentBlocked ||
                !minor ||
                minor > data.summary.balanceAmountMinor ||
                !transferred ||
                !bankReviewed ||
                !consignmentBankReceiptReference(bankReference) ||
                reference.trim().length < 2 ||
                !date
              }
            >
              Record payment
            </button>
          </form>
          <h4>Financial history</h4>
          {!data.events.length && <p>No financial entries.</p>}
          <div className="tcvs-table-wrap">
            <table className="tcvs-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Amount</th>
                  <th>Payment details</th>
                  <th>Recorded (UTC)</th>
                  <th>Administrator / reason</th>
                  <th>Correction</th>
                </tr>
              </thead>
              <tbody>
                {data.events.map((e) => {
                  const voided = data.events.some(
                    (v) => v.action === 'PAYMENT_VOIDED' && v.paymentId === e.paymentId,
                  );
                  return (
                    <tr key={e.requestId}>
                      <td>
                        {e.action.replaceAll('_', ' ')}
                        {e.action === 'PAYMENT_RECORDED' && voided ? ' (voided)' : ''}
                      </td>
                      <td>{money(e.amountMinor)}</td>
                      <td>
                        {e.paymentDate} {e.method?.replaceAll('_', ' ')} {e.reference}
                        {e.bankReceiptReview && (
                          <div>Manual bank receipt evidence: {e.bankReceiptReview.reference}</div>
                        )}
                      </td>
                      <td>{e.at.replace('T', ' ').slice(0, 19)}</td>
                      <td>
                        {e.actor}
                        {e.reason ? ` / ${e.reason}` : ''}
                      </td>
                      <td>
                        {e.action === 'PAYMENT_RECORDED' && !voided && (
                          <button
                            disabled={busy}
                            onClick={() => {
                              setVoidId(e.paymentId);
                              setVoidReason('');
                            }}
                          >
                            Void payment record
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {data.cursor && (
            <button disabled={busy} onClick={() => void run(() => load(data.cursor))}>
              Load older financial entries
            </button>
          )}
          {voidId && (
            <div className="tcvs-statement-actions">
              <h4>Void payment record</h4>
              <p>This corrects bookkeeping only. It does not return transferred money.</p>
              <label>
                Correction reason
                <textarea
                  value={voidReason}
                  maxLength={500}
                  disabled={busy}
                  onChange={(e) => setVoidReason(e.target.value)}
                />
              </label>
              <div className="tcvs-statement-toolbar">
                <button
                  disabled={busy || voidReason.trim().length < 3}
                  onClick={() =>
                    void run(() =>
                      mutate({
                        ...common,
                        operation: 'VOID_STATEMENT_PAYMENT',
                        paymentId: voidId,
                        reason: voidReason,
                      }),
                    )
                  }
                >
                  Confirm void payment record
                </button>
                <button disabled={busy} onClick={() => setVoidId(undefined)}>
                  Cancel
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}
